/**
 * ExitIntentPopup — exit-intent overlay shown on public pages.
 * No data collection, no lead submission, no fabricated consent.
 * Accessible Radix Dialog: named, focus-trapped, Escape closes, focus restored.
 * Simply invites the visitor to explain their need via the existing /contact page.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'bpoContact_exit_popup_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const SHOW_DELAY_MS = 5000;

/** Localised copy — no promise of any downloadable asset. */
const EXIT_COPY: Record<string, { headline: string; sub: string; cta: string; close: string }> = {
  en: { headline: 'Have a project in mind?', sub: 'Tell us about your IT or BPO needs — we will get back to you with a concrete proposal.', cta: 'Contact us', close: 'Close' },
  fr: { headline: 'Un projet en tête\u00a0?', sub: 'Décrivez-nous votre besoin IT ou BPO — nous vous répondrons avec une proposition concrète.', cta: 'Nous contacter', close: 'Fermer' },
  es: { headline: '¿Tiene un proyecto en mente?', sub: 'Cuéntenos su necesidad IT o BPO — le responderemos con una propuesta concreta.', cta: 'Contáctenos', close: 'Cerrar' },
  ar: { headline: 'هل لديك مشروع؟', sub: 'أخبرنا عن احتياجاتك في تكنولوجيا المعلومات أو الاستعانة بمصادر خارجية — سنرد عليك بمقترح ملموس.', cta: 'تواصل معنا', close: 'إغلاق' },
  de: { headline: 'Ein Projekt im Sinn?', sub: 'Beschreiben Sie uns Ihren IT- oder BPO-Bedarf — wir melden uns mit einem konkreten Vorschlag.', cta: 'Kontakt aufnehmen', close: 'Schließen' },
  it: { headline: 'Hai un progetto in mente?', sub: 'Raccontaci le tue esigenze IT o BPO — ti risponderemo con una proposta concreta.', cta: 'Contattaci', close: 'Chiudi' },
  pt: { headline: 'Tem um projeto em mente?', sub: 'Conte-nos a sua necessidade de IT ou BPO — responderemos com uma proposta concreta.', cta: 'Fale connosco', close: 'Fechar' },
  nl: { headline: 'Een project in gedachten?', sub: 'Vertel ons over uw IT- of BPO-behoefte — wij reageren met een concreet voorstel.', cta: 'Neem contact op', close: 'Sluiten' },
  pl: { headline: 'Masz projekt w planach?', sub: 'Opisz swoje potrzeby IT lub BPO — odpowiemy z konkretną propozycją.', cta: 'Skontaktuj się', close: 'Zamknij' },
  tr: { headline: 'Aklınızda bir proje mi var?', sub: 'IT veya BPO ihtiyacınızı bize anlatın — somut bir teklifle dönelim.', cta: 'Bize ulaşın', close: 'Kapat' },
  ru: { headline: 'Есть проект?', sub: 'Расскажите о вашей потребности в IT или BPO — мы ответим конкретным предложением.', cta: 'Связаться с нами', close: 'Закрыть' },
  zh: { headline: '有项目计划吗？', sub: '告诉我们您的IT或BPO需求——我们将以具体方案回复您。', cta: '联系我们', close: '关闭' },
  ja: { headline: 'プロジェクトをお考えですか？', sub: 'ITやBPOのご要望をお聞かせください。具体的なご提案でお応えします。', cta: 'お問い合わせ', close: '閉じる' },
  ko: { headline: '프로젝트를 구상 중이신가요?', sub: 'IT 또는 BPO 요구 사항을 알려주세요 — 구체적인 제안으로 답변드리겠습니다.', cta: '문의하기', close: '닫기' },
  sv: { headline: 'Har du ett projekt i åtanke?', sub: 'Berätta om ditt IT- eller BPO-behov — vi återkommer med ett konkret förslag.', cta: 'Kontakta oss', close: 'Stäng' },
  da: { headline: 'Har du et projekt i tankerne?', sub: 'Fortæl os om dit IT- eller BPO-behov — vi vender tilbage med et konkret forslag.', cta: 'Kontakt os', close: 'Luk' },
};

export function ExitIntentPopup() {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const dismiss = useCallback(() => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
  }, []);

  useEffect(() => {
    // Don't show on admin/portal/auth routes
    if (window.location.pathname.match(/\/(admin|portal|agent|auth|checkout)\//)) return;

    // Check if already dismissed recently
    try {
      const ts = localStorage.getItem(STORAGE_KEY);
      if (ts && Date.now() - Number(ts) < DISMISS_DURATION_MS) return;
    } catch {}

    let triggered = false;
    const handler = (e: MouseEvent) => {
      if (triggered) return;
      if (e.clientY <= 5 && e.movementY < -10) {
        triggered = true;
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setShow(true);
      }
    };

    // Delay listener to avoid triggering on initial page load
    const timer = setTimeout(() => {
      document.addEventListener('mousemove', handler);
    }, SHOW_DELAY_MS);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousemove', handler);
    };
  }, []);

  const onOpenChange = (open: boolean) => {
    if (!open) dismiss();
  };

  const c = EXIT_COPY[language] || EXIT_COPY.en;
  const isRTL = language === 'ar';

  return (
    <DialogPrimitive.Root open={show} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby="exit-popup-desc"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            returnFocusRef.current?.focus?.();
          }}
          dir={isRTL ? 'rtl' : undefined}
          className="fixed left-1/2 top-1/2 z-[201] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-card border border-border p-8 shadow-2xl focus:outline-none"
        >
          <DialogPrimitive.Close
            aria-label={c.close}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </DialogPrimitive.Close>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-primary" aria-hidden="true" />
            </div>
          </div>

          <DialogPrimitive.Title className="text-xl font-bold text-foreground mb-2">{c.headline}</DialogPrimitive.Title>
          <DialogPrimitive.Description id="exit-popup-desc" className="text-muted-foreground text-sm mb-6">{c.sub}</DialogPrimitive.Description>

          <Button asChild className="w-full h-12 font-semibold">
            <Link to={`/${language}/contact`} onClick={() => onOpenChange(false)}>
              {c.cta}
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} aria-hidden="true" />
            </Link>
          </Button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default ExitIntentPopup;
